import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import { observer } from "mobx-react-lite";
import VehicleRegisterDetailInfoViewModel from "viewModels/callCenter/search/vehicleRegister/vehicleRegisterDetail/vehicleRegisterDetailInfoViewModel";
import { Col, Form, Row } from "react-bootstrap";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatDate, formatDateTime } from "utils/localizations";
import { emptyValue } from "utils/emptyValue";
import { vehicleRegisterVehicleStatusToVariant } from "entities/vehicleRegisterVehicleStatus";
import { FormLabelWithTooltip } from "controls/formLabelWithTooltip";

const getTranslatedFieldLabel = (vm: VehicleRegisterDetailInfoViewModel, field: string) => {
  return vm.translateSubAttribute("vehicleRegisterDetailInfo", field);
};

const getFormattedValue = <T, R>(value: T, formatter: (value: T) => R): R | typeof emptyValue => {
  return value ? formatter(value) : emptyValue;
};

const FieldGroup = ({
  label,
  target,
  property,
  valueFormatter,
  size,
  tooltip,
}: {
  label: string;
  target: any;
  property: string;
  valueFormatter: (value: any) => string;
  size?: number;
  tooltip?: string;
}) => {
  return (
    <Form.Group as={Col} md={size ?? 6} xl={size ?? 6}>
      {tooltip ? <FormLabelWithTooltip tooltipText={tooltip}>{label}</FormLabelWithTooltip> : <Form.Label>{label}</Form.Label>}
      <FakeInput target={target} property={property} valueFormatter={valueFormatter} />
    </Form.Group>
  );
};

const formFields = [
  {
    label: "externalVehicleIdent",
    property: "externalVehicleIdent",
    formatter: (value: any) => value ?? emptyValue,
    size: 4,
    tooltip: "externalVehicleIdentTooltip",
  },
  { label: "vin", property: "vin", formatter: (value: any) => value ?? emptyValue, size: 8 },
  {
    label: "countryId",
    property: "countryId",
    formatter: (value: any, vm: VehicleRegisterDetailInfoViewModel) => (value ? vm.translateCountryId(value) : emptyValue),
    size: 3,
  },
  { label: "licensePlate", property: "licensePlate", formatter: (value: any) => value ?? emptyValue, size: 3 },
  {
    label: "licensePlateValidSinceDate",
    property: "licensePlateValidSinceDate",
    formatter: (value: any) => getFormattedValue(value, formatDate),
    size: 3,
  },
  {
    label: "licensePlateValidUntilDate",
    property: "licensePlateValidUntilDate",
    formatter: (value: any) => getFormattedValue(value, formatDate),
    size: 3,
  },
  {
    label: "vehicleStatus",
    property: "vehicleStatus",
    formatter: (value: any, vm: VehicleRegisterDetailInfoViewModel) =>
      value ? (
        <strong className={`text-${vehicleRegisterVehicleStatusToVariant(value)}`}>{vm.translateVehicleStatus(value)}</strong>
      ) : (
        emptyValue
      ),
    size: 4,
    tooltip: "vehicleStatusTooltip",
  },
  {
    label: "vehicleStatusValidSinceDate",
    property: "vehicleStatusValidSinceDate",
    formatter: (value: any) => getFormattedValue(value, formatDate),
    size: 4,
  },
  {
    label: "vehicleStatusValidUntilDate",
    property: "vehicleStatusValidUntilDate",
    formatter: (value: any) => getFormattedValue(value, formatDate),
    size: 4,
  },
  { label: "category", property: "category", formatter: (value: any) => value ?? emptyValue, size: 3 },
  { label: "categoryName", property: "categoryName", formatter: (value: any) => value ?? emptyValue, size: 3 },
  {
    label: "maximumAllowedWeight",
    property: "maximumAllowedWeight",
    formatter: (value: any, vm: VehicleRegisterDetailInfoViewModel) => (value ? vm.formatHeight(value) : emptyValue),
    size: 6,
  },
  { label: "emissionsCo2", property: "emissionsCo2", formatter: (value: any) => value ?? emptyValue, size: 4 },
  {
    label: "fuelTypes",
    property: "fuelTypes",
    formatter: (value: any, vm: VehicleRegisterDetailInfoViewModel) =>
      value?.length > 0 ? vm.translateVehicleRegisterVehicleFuelType(value) : emptyValue,
    size: 8,
  },
  { label: "fuelTypeName1", property: "fuelTypeName1", formatter: (value: any) => value ?? emptyValue, size: 4 },
  { label: "fuelTypeName2", property: "fuelTypeName2", formatter: (value: any) => value ?? emptyValue, size: 4 },
  { label: "fuelTypeName3", property: "fuelTypeName3", formatter: (value: any) => value ?? emptyValue, size: 4 },
  {
    label: "classifiedFuelType",
    property: "classifiedFuelType",
    formatter: (value: any, vm: VehicleRegisterDetailInfoViewModel) =>
      value ? vm.translateVehicleRegisterVehicleClassifiedFuelType(value) : emptyValue,
    size: 4,
  },
  { label: "updatedAt", property: "updatedAt", formatter: (value: any) => getFormattedValue(value, formatDateTime), size: 4 },
  {
    label: "createdAt",
    property: "createdAt",
    formatter: (value: any) => getFormattedValue(value, formatDateTime),
    size: 4,
    tooltip: "createdAtTooltip",
  },
];

export const VehicleRegisterDetailInfoView: ViewComponent<VehicleRegisterDetailInfoViewModel> = observer(({ vm }) => {
  if (!vm.item) {
    return (
      <LoaderOverlay loading={true}>
        <div style={{ minHeight: 200 }}></div>
      </LoaderOverlay>
    );
  }

  return (
    <div className="d-flex">
      <Col className="p-0">
        <Col lg={12} className="pr-3 pl-0">
          <div className="bg-secondary px-4 px-lg-5 py-3 rounded-top text-white">
            <span className="font-size-lg font-weight-bold">{vm.sectionName}</span>
          </div>
          <div className="bg-light mb-4 px-4 px-lg-5 pt-4 pb-2 rounded-bottom">
            <Row>
              {formFields.map(({ label, property, formatter, size, tooltip }) => (
                <FieldGroup
                  key={label}
                  label={getTranslatedFieldLabel(vm, label)}
                  target={vm.item}
                  property={property}
                  size={size}
                  valueFormatter={(value: any) => formatter(value, vm)}
                  tooltip={tooltip ? vm.translate(tooltip) : ""}
                />
              ))}
            </Row>
          </div>
        </Col>
      </Col>
    </div>
  );
});

registerView(VehicleRegisterDetailInfoView, VehicleRegisterDetailInfoViewModel);
